// Shared helpers for every custom cart UI (hover drawer, cart pop-up, product
// cards, product page). This file loads on every page from the header, so it
// is the one place they can all rely on.
window.VoraxCart =
  window.VoraxCart ||
  (() => {
    const ICON_SECTION = "cart-icon-bubble";
    const REQUEST_TIMEOUT = 20000;
    const FALLBACK_ERROR =
      "There was an error while updating your cart. Please try again.";

    function strings() {
      return window.cartStrings || {};
    }

    function cartError(message, status) {
      return Object.assign(
        new Error(message || strings().error || FALLBACK_ERROR),
        { status },
      );
    }

    // Keeps the /es/ prefix on every request.
    function url(path) {
      return `${window.Shopify?.routes?.root || "/"}${path}`;
    }

    // Same shape as fetchConfig() in global.js, which is deferred and so may
    // not exist yet when a shopper taps early.
    function config(body) {
      const options = { headers: { Accept: "application/json" } };

      if (body !== undefined) {
        options.method = "POST";
        options.headers["Content-Type"] = "application/json";
        options.body = JSON.stringify(body);
      }

      // A request stuck on venue Wi-Fi must not freeze the buttons forever.
      if (typeof AbortSignal !== "undefined" && AbortSignal.timeout) {
        options.signal = AbortSignal.timeout(REQUEST_TIMEOUT);
      }

      return options;
    }

    async function parse(response) {
      try {
        return await response.json();
      } catch (error) {
        return {};
      }
    }

    async function request(path, body) {
      let response;

      try {
        response = await fetch(url(path), config(body));
      } catch (error) {
        throw cartError(null, 0);
      }

      const data = await parse(response);

      if (!response.ok) {
        throw cartError(
          response.status === 429
            ? strings().busy
            : data.description || data.message,
          response.status,
        );
      }

      return data;
    }

    function isCart(data) {
      return (
        Boolean(data) &&
        Array.isArray(data.items) &&
        typeof data.item_count === "number"
      );
    }

    async function getCart() {
      const cart = await request("cart.js");

      if (!isCart(cart)) throw cartError(null, 0);

      return cart;
    }

    // Extra body fields that make /cart/add.js and /cart/change.js return the
    // header cart icon already rendered, as Dawn's product form does.
    function withSections(body) {
      return {
        ...body,
        sections: ICON_SECTION,
        sections_url: window.location.pathname,
      };
    }

    function renderCartIcon(sections) {
      const html = sections?.[ICON_SECTION];
      const icon = document.getElementById(ICON_SECTION);

      if (!html || !icon) return false;

      const source = new DOMParser()
        .parseFromString(html, "text/html")
        .querySelector(".shopify-section");

      if (!source) return false;

      icon.innerHTML = source.innerHTML;

      return true;
    }

    // Fallback when no rendered icon came back (e.g. after a failed request).
    // The accessible "N items" label returns with the next rendered icon.
    function updateCartCount(cart) {
      if (!isCart(cart)) return;

      const icon =
        document.getElementById(ICON_SECTION) ||
        document.querySelector(".header__icon--cart");

      if (!icon) return;

      let bubble = icon.querySelector(".cart-count-bubble");

      if (cart.item_count === 0) {
        bubble?.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement("div");
        bubble.className = "cart-count-bubble";
        icon.appendChild(bubble);
      }

      bubble.innerHTML = `<span aria-hidden="true">${
        cart.item_count < 100 ? cart.item_count : ""
      }</span><span class="visually-hidden">${cart.item_count}</span>`;
    }

    // Pushes a fresh cart to the header icon and the hover drawer. Anything
    // that isn't a cart (an add.js line item, an error body) is ignored.
    function syncCart(cart, sections) {
      if (!isCart(cart)) return;

      if (!renderCartIcon(sections)) updateCartCount(cart);

      window.CartDrawer?.setCart?.(cart);

      // Lets the /cart sections re-render after a drawer change.
      if (
        typeof publish === "function" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "vorax-cart",
          cartData: cart,
        });
      }
    }

    // Formats from window.moneyFormats (cart-drawer-vorax.liquid): the
    // shopper's currency as set up in admin, e.g. "$ 1,475.00" for MXN and
    // "$262.000,00" for COP. Same output as Liquid's money filters.
    const MONEY_PATTERNS = {
      amount: [2, ",", "."],
      amount_no_decimals: [0, ",", "."],
      amount_with_comma_separator: [2, ".", ","],
      amount_no_decimals_with_comma_separator: [0, ".", ","],
      amount_with_apostrophe_separator: [2, "'", "."],
      amount_no_decimals_with_space_separator: [0, " ", ""],
      amount_with_space_separator: [2, " ", ","],
      amount_with_period_and_space_separator: [2, " ", "."],
    };

    function formatAmount(cents, [decimals, thousands, decimal]) {
      const [whole, fraction] = (Math.abs(cents) / 100)
        .toFixed(decimals)
        .split(".");
      const sign = cents < 0 ? "-" : "";

      return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)}${
        fraction ? decimal + fraction : ""
      }`;
    }

    // withCurrency: use money_with_currency (e.g. "$232.00 CAD") when the
    // theme's "Show currency codes" setting is on, as the /cart totals do.
    function formatMoney(cents, withCurrency = false) {
      const formats = window.moneyFormats || {};
      const format =
        withCurrency && formats.showCurrency
          ? formats.withCurrency
          : formats.money;
      const value = Number(cents) || 0;

      if (!format) {
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency: window.shopCurrency || window.Shopify?.currency?.active,
        }).format(value / 100);
      }

      return format
        .replace(/<[^>]*>/g, "")
        .replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) =>
          formatAmount(value, MONEY_PATTERNS[name] || MONEY_PATTERNS.amount),
        );
    }

    // Labels for the cart UIs, from window.cartStrings in theme.liquid so they
    // follow the storefront language. English stands in if that script hasn't
    // run yet.
    const LABELS = {
      subtotal: "Subtotal",
      discount: "Discount",
      total: "Total",
      remove: "Remove [title]",
    };

    function label(key, title = "") {
      return (strings()[key] || LABELS[key]).replace("[title]", title);
    }

    // Shown when the cart came back with fewer units than were asked for.
    function quantityError(quantity) {
      return (
        strings().quantityError ||
        "You can only add [quantity] of this item to your cart."
      ).replace("[quantity]", quantity);
    }

    function imageUrl(src, width) {
      if (!src) return "";

      try {
        const image = new URL(src, window.location.origin);

        image.searchParams.set("width", width);

        return image.href;
      } catch (error) {
        return src;
      }
    }

    return {
      url,
      request,
      getCart,
      isCart,
      withSections,
      syncCart,
      updateCartCount,
      formatMoney,
      label,
      quantityError,
      imageUrl,
    };
  })();

window.syncCart = window.VoraxCart.syncCart;
window.updateCartCount = window.VoraxCart.updateCartCount;



class CartDrawer {
  constructor(drawer) {
    this.trigger = document.querySelector(".header__icon--cart");
    this.drawer = drawer;
    this.items = this.drawer.querySelector(".cart-items");
    this.count = this.drawer.querySelector(".cart-count");
    this.checkoutBtn = this.drawer.querySelector(".cart-checkout-btn");
    this.summary = this.drawer.querySelector(".cart-summary");
    this.error = this.drawer.querySelector(".cart-error");

    this.isHoveringTrigger = false;
    this.isHoveringDrawer = false;

    this.cart = null;

    // One request at a time; every change re-renders from the cart it returns.
    this.busy = false;

    // Bumped by every setCart(), so a slow change can tell that a newer cart
    // (e.g. from an add) landed meanwhile and must not be overwritten.
    this.version = 0;

    this.openTimeout = null;
    this.closeTimeout = null;

    if (!this.trigger) return;

    this.bindEvents();
  }

  bindEvents() {

    if (!window.matchMedia("(min-width: 1024px)").matches) return;

    this.trigger.addEventListener("mouseenter", () => {
      this.isHoveringTrigger = true;

      this.handleMouseEnter();
    });

    this.trigger.addEventListener("mouseleave", () => {
      this.isHoveringTrigger = false;

      this.handleMouseLeave();
    });

    this.drawer.addEventListener("mouseenter", () => {
      this.isHoveringDrawer = true;

      clearTimeout(this.closeTimeout);
    });

    this.drawer.addEventListener("mouseleave", () => {
      this.isHoveringDrawer = false;

      this.handleMouseLeave();
    });

    this.items.addEventListener("click", this.handleItemsClick.bind(this));

    this.checkoutBtn.addEventListener("click", this.handleCheckout.bind(this));
  }

  handleMouseEnter() {
    clearTimeout(this.closeTimeout);

    this.openTimeout = setTimeout(() => {
      this.open();
    }, 150);
  }

  handleMouseLeave() {
    clearTimeout(this.openTimeout);

    this.closeTimeout = setTimeout(() => {
      if (!this.isHoveringTrigger && !this.isHoveringDrawer) {
        this.close();
      }
    }, 250);
  }

  // Always re-fetches, because the cart may have changed on /cart, in Dawn's
  // notification or in another tab. A cached cart shows straight away.
  async open() {
    if (this.cart) this.drawer.classList.add("cart-open");

    if (!this.busy) {
      this.showError();

      await this.loadCart();
    }

    this.drawer.classList.add("cart-open");
  }

  close() {
    this.drawer.classList.remove("cart-open");
  }

  async loadCart() {
    try {
      this.setCart(await VoraxCart.getCart());

      this.setLoading(false);
    } catch (error) {
      if (!this.cart) this.showError(error.message);
    }
  }

  setCart(cart) {
    if (!VoraxCart.isCart(cart)) return;

    this.cart = cart;

    this.version += 1;

    this.render();
  }

  handleCheckout() {
    if (this.busy || !this.cart || this.cart.item_count === 0) {
      return;
    }

    window.location.href = VoraxCart.url("checkout");
  }

  handleItemsClick(event) {
    const button = event.target.closest(".cart-remove-btn, .cart-qty-btn");

    if (!button || this.busy) return;

    const item = this.cart?.items.find(
      (item) => item.key === button.dataset.key,
    );

    if (!item) return;

    let quantity = 0;

    if (button.classList.contains("cart-qty-btn")) {
      quantity =
        button.dataset.action === "increase"
          ? item.quantity + 1
          : item.quantity - 1;
    }

    this.updateQuantity(item.key, quantity, button.closest(".cart-item"));
  }

  async updateQuantity(key, quantity, row) {
    this.busy = true;

    const version = this.version;

    this.showError();
    this.setLoading(true, row);

    try {
      const cart = await VoraxCart.request(
        "cart/change.js",
        VoraxCart.withSections({ id: key, quantity }),
      );

      if (version !== this.version) {
        await this.loadCart();
        return;
      }

      // syncCart() also renders this drawer.
      if (window.syncCart) window.syncCart(cart, cart.sections);
      else this.setCart(cart);

      const line = cart.items.find((item) => item.key === key);

      if (line && line.quantity < quantity) {
        this.showError(VoraxCart.quantityError(line.quantity));
      }
    } catch (error) {
      this.showError(error.message);

      const cart = await VoraxCart.getCart().catch(() => null);

      if (window.syncCart) window.syncCart(cart);
      else this.setCart(cart);
    } finally {
      this.busy = false;

      this.setLoading(false, row);
    }
  }

  setLoading(loading, row) {
    row?.classList.toggle("pointer-events-none", loading);
    row?.classList.toggle("opacity-50", loading);

    this.checkoutBtn.disabled = loading;
    this.checkoutBtn.classList.toggle("opacity-50", loading);
    this.checkoutBtn.classList.toggle("pointer-events-none", loading);
  }

  // No message hides the alert.
  showError(message) {
    if (!this.error) return;

    this.error.textContent = message || "";

    this.error.classList.toggle("hidden", !message);
  }

  renderOptions(item) {
    return item.options_with_values
      .map(
        (option) => `
            <span class="text-[13px]">
                ${option.name}: ${option.value}
            </span>
        `,
      )
      .join("");
  }
  renderItem(item) {
    return `

<article
class="cart-item flex! mt-8 gap-8 font-inter-light relative"
data-key="${item.key}"
>

    ${item.image ? `<img
        src="${VoraxCart.imageUrl(item.image, 200)}"
        alt="${item.product_title}"
        loading="lazy"
        class="max-h-[100px] h-[100px] w-[100px] object-cover rounded-lg"
    >` : ""}

    <button
    type="button"
    class="cart-remove-btn absolute top-1/2 -translate-y-1/2 right-15 border rounded-full p-2"
    data-key="${item.key}"
        aria-label="${VoraxCart.label("remove", item.product_title)}">

        <img class="h-6! w-6!" src="https://cdn.shopify.com/s/files/1/0835/8615/4752/files/cross.svg?v=1785704340">

    </button>

    <div class="flex flex-col flex-1">

        <h4 class="font-inter-bold! text-[16px]!">

            ${item.product_title}

        </h4>
        <div class="flex flex-col">
            ${this.renderOptions(item)}
        </div>


        <div>

            <span class="font-bebasneue text-[20px]">${VoraxCart.formatMoney(item.final_line_price)}</span>

        </div>

        <div class="cart-quantity flex items-center mt-4 gap-2 bg-primary-cream w-fit shadow-qty-button rounded-lg font-inter-bold px-4 gap-4">

            <button
                type="button"
                class="cart-qty-btn cursor-pointer"
                data-action="decrease"
                data-key="${item.key}">

                -

            </button>

            <span>

                ${item.quantity}

            </span>

            <button
                type="button"
                class="cart-qty-btn cursor-pointer"
                data-action="increase"
                data-key="${item.key}">

                +

            </button>

        </div>

    </div>

</article>

`;
  }
  renderItems() {
    this.items.innerHTML = this.cart.items
      .map((item) => this.renderItem(item))
      .join("");
  }
  renderCount() {
    this.count.textContent = `(${this.cart.item_count})`;
  }

  renderFooter() {
    const hasDiscount = this.cart.original_total_price > this.cart.total_price;

    if (!hasDiscount) {
      this.summary.innerHTML = `
            <div class="flex items-center justify-between text-base font-bebasneue text-[20px]">

                <span>${VoraxCart.label("subtotal")}</span>

                <span>${VoraxCart.formatMoney(this.cart.total_price, true)}</span>

            </div>
        `;

      return;
    }

    const discount = this.cart.original_total_price - this.cart.total_price;

    this.summary.innerHTML = `
        <div class="font-bebasneue text-[20px]">

            <div class="flex justify-between">

                <span class="">
                    ${VoraxCart.label("subtotal")}
                </span>

                <span class="line-through ">
                     ${VoraxCart.formatMoney(this.cart.original_total_price)}
                </span>

            </div>

            <div class="flex justify-between">

                <span>

                    ${VoraxCart.label("discount")}
                    ${
                      this.cart.cart_level_discount_applications.length
                        ? `(${this.cart.cart_level_discount_applications
                            .map((d) => d.title)
                            .join(", ")})`
                        : ""
                    }

                </span>

                <span>

                    -${VoraxCart.formatMoney(discount)}

                </span>

            </div>

            <div class="border-t pt-2 flex justify-between">

                <span>${VoraxCart.label("total")}</span>

                <span>${VoraxCart.formatMoney(this.cart.total_price, true)}</span>

            </div>

        </div>
    `;
  }

  render() {
    this.renderItems();

    this.renderFooter();

    this.renderCount();
  }
}

// The drawer markup is rendered just above this script in the header, so it
// can be wired up straight away and catch the earliest add to cart.
if (document.querySelector("#CartDrawer")) {
  window.CartDrawer = new CartDrawer(document.querySelector("#CartDrawer"));
}
